import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Send, Search } from "lucide-react";

/**
 * GameInviteModal — pick a friend or type a username to invite them to the
 * current game room. Fired by the game screen when the user clicks
 * "Invite friend".
 *
 * Props:
 *   open      — boolean
 *   onClose   — fn()
 *   roomId    — string (must already exist server-side)
 *   gameName  — display name of the game (for the invite copy)
 *   onSent    — fn(username) — called after successful invite
 */
export default function GameInviteModal({ open, onClose, roomId, gameName, onSent }) {
  const [friends, setFriends] = useState([]);
  const [q, setQ] = useState("");
  const [suggest, setSuggest] = useState([]);
  const [busy, setBusy] = useState("");
  const [sentTo, setSentTo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSentTo(null); setError(""); setQ(""); setSuggest([]);
    api.get("/friends").then(r => setFriends(r.data || [])).catch(() => setFriends([]));
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setSuggest([]); return; }
    const t = setTimeout(() => {
      api.get(`/users/lookup?q=${encodeURIComponent(q.trim())}&limit=6`)
        .then(r => setSuggest(r.data || []))
        .catch(() => setSuggest([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const invite = async (username) => {
    if (!roomId || !username) return;
    setBusy(username); setError("");
    try {
      await api.post(`/games/rooms/${roomId}/invite`, { username });
      setSentTo(username);
      onSent && onSent(username);
    } catch (e) {
      setError(e?.response?.data?.detail || "Invite failed");
    } finally { setBusy(""); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="game-invite-modal">
      <div className="w-full max-w-md sc-card p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white" data-testid="game-invite-close"><X className="w-4 h-4" /></button>
        <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Invite a friend</div>
        <h2 className="sc-heading text-2xl font-black mt-1 mb-1">Play {gameName} together</h2>
        <p className="text-xs text-zinc-500 mb-4 sc-mono">They&apos;ll get a real-time invite. You&apos;ll play as cyan (host), they&apos;ll play as pink.</p>

        {sentTo ? (
          <div className="p-4 rounded-sm bg-[color:var(--sc-volt)]/10 border border-[color:var(--sc-volt)]/30 text-sm">
            <div className="font-bold" style={{ color: "var(--sc-volt)" }}>Invite sent to @{sentTo}</div>
            <div className="text-xs text-zinc-400 mt-1">Waiting for them to join…</div>
          </div>
        ) : (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <input
                data-testid="game-invite-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Type a username or MC-code…"
                className="sc-input w-full !pl-9 text-sm"
              />
            </div>
            {suggest.length > 0 && (
              <div className="mb-3 space-y-1">
                {suggest.map(u => (
                  <button
                    key={u.id}
                    disabled={!!busy}
                    onClick={() => invite(u.username)}
                    data-testid={`game-invite-suggest-${u.username}`}
                    className="w-full flex items-center justify-between p-2 rounded-sm border border-white/10 hover:border-white/25 hover:bg-white/5 transition text-left"
                  >
                    <span className="text-sm">@{u.username} <span className="text-[10px] text-zinc-500 sc-mono">{u.user_code}</span></span>
                    <span className="sc-mono text-[10px] tracking-widest uppercase text-[color:var(--sc-pink)]">
                      {busy === u.username ? "…" : <><Send className="w-3 h-3 inline mr-1" />Invite</>}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {friends.length > 0 && (
              <>
                <div className="text-[10px] sc-mono tracking-widest uppercase text-zinc-500 mb-2">Your friends</div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {friends.map(f => (
                    <button
                      key={f.username}
                      disabled={!!busy}
                      onClick={() => invite(f.username)}
                      data-testid={`game-invite-friend-${f.username}`}
                      className="w-full flex items-center justify-between p-2 rounded-sm border border-white/10 hover:border-[color:var(--sc-pink)]/50 hover:bg-white/5 transition text-left"
                    >
                      <span className="text-sm">@{f.username}</span>
                      <span className="sc-mono text-[10px] tracking-widest uppercase text-[color:var(--sc-pink)]">
                        {busy === f.username ? "Sending…" : <><Send className="w-3 h-3 inline mr-1" />Invite</>}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {friends.length === 0 && !q && (
              <div className="text-xs text-zinc-500 sc-mono">You have no friends yet. Add someone in Messages first, or type their username above.</div>
            )}
            {error && <div className="text-xs text-[color:var(--sc-pink)] mt-2 sc-mono" data-testid="game-invite-error">{error}</div>}
          </>
        )}
      </div>
    </div>
  );
}
